import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Radio,
  notification,
  Tooltip,
  Checkbox,
  Tag,
  InputNumber,
  DatePicker,
} from "antd";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { Modal } from "components/General/ModalFormComponents";
import styles from "./style.module.scss";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import { resolveQuotaNotApplicableMessage } from "util/quotaCheckDisplay";
import SingleSelect from "helperComponents/SingleSelect";
import MultipleSelect from "helperComponents/MultipleSelect";
import SearchableTopicChecklist from "helperComponents/SearchableTopicChecklist";
import dayjs from "dayjs";

const CreateReport = ({
  open,
  onClose,
  getReportsDownloads,
  createLoading,
  setCreateLoading,
  cancelButtonText = "Back",
  saveButtonText = "Next",
  theme,
}) => {
  const { t } = useTranslation();
  const [isMobileView, setIsMobileView] = useState(
    window.innerWidth < 950 ? true : false,
  );

  // Step 0: Generation Type
  const [generationType, setGenerationType] = useState(null); // 'ONE_TIME' | 'PERIODICAL'

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 950) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileView]);

  // Step 1: Report Name
  const [reportName, setReportName] = useState("");

  // Step 2: Report Type
  const [reportKind, setReportKind] = useState(null); // 'STANDARD' | 'AI_ADVANCED'

  // AI Quota Check (automatic for AI_ADVANCED)
  const [quotaCheckResult, setQuotaCheckResult] = useState(null);
  const [quotaCheckLoading, setQuotaCheckLoading] = useState(false);
  const [quotaCheckDeps, setQuotaCheckDeps] = useState(null); // Track dependencies for quota check

  // Step 3: Group and Dashboards
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [dashboardsLoading, setDashboardsLoading] = useState(false);

  // Step 4A: One-time (Date Period) - consolidated date state
  const [daysRange, setDaysRange] = useState("last7d");
  const [reportDateRange, setReportDateRange] = useState([]);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  // Step 4B: Periodical (Frequency)
  const [frequency, setFrequency] = useState(null); // 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'

  // Step 5: Pivot Values
  const [pivotCols, setPivotCols] = useState([]);
  const [selectedPivotCol, setSelectedPivotCol] = useState(null);
  const [pivotValues, setPivotValues] = useState([]);
  const [selectedPivotValues, setSelectedPivotValues] = useState([]);
  const [generateIndividualReports, setGenerateIndividualReports] = useState(
    false,
  );
  const [pivotsLoading, setPivotsLoading] = useState(false);
  const [pivotValuesLoading, setPivotValuesLoading] = useState(false);

  // Keys to force re-render of selects
  const [dashboardSelectKey, setDashboardSelectKey] = useState(0);
  const [pivotValuesSelectKey, setPivotValuesSelectKey] = useState(0);

  // Step 6: Full Report vs Selected Topics
  const [reportScope, setReportScope] = useState(null); // 'FULL' | 'SELECTED_TOPICS'
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);

  // Step 7: Recipient Emails
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  const [currStep, setCurrStep] = useState(0);

  // Date range presets
  const rangePresets = [
    {
      id: "last7d",
      label: t("filter.16") || "Last 7 days",
      value: [dayjs().subtract(6, "day"), dayjs()],
    },
    {
      id: "last14d",
      label: t("filter.17") || "Last 14 days",
      value: [dayjs().subtract(13, "day"), dayjs()],
    },
    {
      id: "last30d",
      label: t("filter.18") || "Last 30 days",
      value: [dayjs().subtract(29, "day"), dayjs()],
    },
    {
      id: "last90d",
      label: t("filter.19") || "Last 90 days",
      value: [dayjs().subtract(89, "day"), dayjs()],
    },
  ];

  // Initialize default date range on mount
  useEffect(() => {
    if (daysRange === "last7d" && reportDateRange.length === 0) {
      const defaultPreset = rangePresets.find((p) => p.id === "last7d");
      if (defaultPreset) {
        const dateString = [
          dayjs(defaultPreset.value[0]).format("YYYY-MM-DD"),
          dayjs(defaultPreset.value[1]).format("YYYY-MM-DD"),
        ];
        setReportDateRange(dateString);
        setSince(dateString[0]);
        setUntil(dateString[1]);
      }
    }
  }, []);

  // Auto-apply custom date changes
  useEffect(() => {
    if (daysRange === "custom" && since && until) {
      setReportDateRange([since, until]);
    }
  }, [since, until, daysRange]);

  /** Resolves since/until for ONE_TIME: presets last7d–last90d or custom from state. */
  const resolveOneTimeDateRange = () => {
    if (daysRange === "custom") {
      if (reportDateRange?.length >= 2) {
        return { since: reportDateRange[0], until: reportDateRange[1] };
      }
      if (since && until) {
        return { since, until };
      }
      return null;
    }
    const preset = rangePresets.find((p) => p.id === daysRange);
    if (!preset) {
      return null;
    }
    return {
      since: dayjs(preset.value[0]).format("YYYY-MM-DD"),
      until: dayjs(preset.value[1]).format("YYYY-MM-DD"),
    };
  };

  const getGroups = async () => {
    setGroupsLoading(true);
    const response = await apiRequest(
      "industry_analysis/groups",
      "GET",
      null,
      true,
    );
    if (response?.data) {
      const gr = response?.data;
      setGroups(
        gr?.map((group) => ({
          id: group.groupId,
          label: group.name,
          value: group.groupId,
        })),
      );
    } else {
      setGroups([]);
      notification.error({
        message: response?.error ? response?.error : "Failed to get groups",
      });
    }
    setGroupsLoading(false);
  };

  const getDashboards = async () => {
    setDashboardsLoading(true);
    const response = await apiRequest(
      "industry_analysis/dashboards",
      "POST",
      { groupIds: [selectedGroup?.id] },
      true,
    );
    if (response?.data) {
      const dash = response?.data;
      setDashboards(
        dash?.map((dashboard) => ({
          id: dashboard.dashboardId,
          label: dashboard.name,
          value: dashboard.dashboardId,
        })),
      );
    } else {
      setDashboards([]);
      notification.error({
        message: response?.error ? response?.error : "Failed to get dashboards",
      });
    }
    setDashboardsLoading(false);
  };

  const getTopics = async () => {
    setTopicsLoading(true);
    const response = await apiRequest(
      "industry_analysis/categories",
      "POST",
      {
        dashboard_ids: selectedDashboards
          ?.filter(Boolean)
          .map((dashboard) => dashboard.id),
      },
      true,
    );
    if (response?.data) {
      const tpcs = response?.data;
      setTopics(
        tpcs?.map((topic) => ({
          id: topic.categoryId,
          label: topic.name,
          value: topic.categoryId,
        })),
      );
    } else {
      setTopics([]);
      notification.error({
        message: response?.error ? response?.error : "Failed to get topics",
      });
    }
    setTopicsLoading(false);
  };

  const getPivots = async () => {
    setPivotsLoading(true);
    const response = await apiRequest(
      "report/pivots",
      "POST",
      {
        dashboard_ids: selectedDashboards
          ?.filter(Boolean)
          .map((dashboard) => dashboard.id),
      },
      true,
    );
    if (response?.success) {
      const data = response.success;
      const cols = [];
      data.forEach((item) => {
        Object.entries(item).forEach(([id, value]) => {
          Object.keys(value).forEach((key) => {
            cols.push({
              id: key,
              label: key,
              value: key,
            });
          });
        });
      });
      // Remove duplicates
      const uniqueCols = cols.filter(
        (col, index, self) => index === self.findIndex((c) => c.id === col.id),
      );
      setPivotCols(uniqueCols);
    } else {
      setPivotCols([]);
      notification.error({
        message: response?.error ? response?.error : "Failed to get pivots",
      });
    }
    setPivotsLoading(false);
  };

  const getPivotValues = async (pivotColumn) => {
    setPivotValuesLoading(true);
    const response = await apiRequest(
      "report/pivots",
      "POST",
      {
        dashboard_ids: selectedDashboards
          ?.filter(Boolean)
          .map((dashboard) => dashboard.id),
      },
      true,
    );
    if (response?.success) {
      const data = response.success;
      const values = new Set();

      data.forEach((item) => {
        Object.entries(item).forEach(([id, value]) => {
          if (value[pivotColumn]) {
            Object.keys(value[pivotColumn]).forEach((val) => {
              values.add(val);
            });
          }
        });
      });

      const valueOptions = Array.from(values).map((val) => ({
        id: val,
        label: val,
        value: val,
      }));

      setPivotValues(valueOptions);
    } else {
      setPivotValues([]);
      notification.error({
        message: response?.error
          ? response?.error
          : "Failed to get pivot values",
      });
    }
    setPivotValuesLoading(false);
  };

  const checkQuotaEstimate = async () => {
    setQuotaCheckLoading(true);

    const payload = {
      quota_type: "advanced_ai",
      dashboard_ids: selectedDashboards
        ?.filter(Boolean)
        .map((dashboard) => dashboard.id.toString()),
    };
    if (selectedPivotCol && selectedPivotValues.length > 0) {
      const validPivotValues = selectedPivotValues.filter(Boolean);

      // New structure: pivot_key and pivot_values
      payload.pivot_key = selectedPivotCol.id;
      payload.pivot_values = validPivotValues.map((v) => v.id);
    }

    if (generationType === "ONE_TIME") {
      const dr = resolveOneTimeDateRange();
      if (dr) {
        payload.start_date = dr.since;
        payload.end_date = dr.until;
      } else {
        const fallback = rangePresets.find((p) => p.id === "last7d");
        if (fallback) {
          payload.start_date = dayjs(fallback.value[0]).format("YYYY-MM-DD");
          payload.end_date = dayjs(fallback.value[1]).format("YYYY-MM-DD");
        }
      }
    }

    if (generationType === "PERIODICAL") {
      let startDate, endDate;
      
      if (frequency === "WEEKLY") {
        // Last week (Monday to Sunday)
        startDate = dayjs().subtract(1, 'week').startOf('week').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'week').endOf('week').format("YYYY-MM-DD");
      } else if (frequency === "MONTHLY") {
        // Last month
        startDate = dayjs().subtract(1, 'month').startOf('month').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'month').endOf('month').format("YYYY-MM-DD");
      } else if (frequency === "QUARTERLY") {
        // Last quarter
        startDate = dayjs().subtract(1, 'quarter').startOf('quarter').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'quarter').endOf('quarter').format("YYYY-MM-DD");
      } else {
        // Default to last month if frequency not specified
        startDate = dayjs().subtract(1, 'month').startOf('month').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'month').endOf('month').format("YYYY-MM-DD");
      }
      
      payload.start_date = startDate;
      payload.end_date = endDate;
    }

    const response = await apiRequest(
      "subscriptions/check_quota_for_target_params",
      "POST",
      payload,
      true,
    );

    setQuotaCheckLoading(false);
    setQuotaCheckResult(response);

    return response;
  };

  const handleCreateReport = async () => {
    setCreateLoading(true);
    const payload = {
      generation_type: generationType,
      report_name: reportName,
      report_kind: reportKind,
      group_id: parseInt(selectedGroup?.id),
      dashboard_ids: selectedDashboards
        ?.filter(Boolean)
        .map((dashboard) => dashboard.id),
    };

    if (recipientEmails.length > 0) {
      payload.recipient_emails = recipientEmails;
    }

    if (generationType === "ONE_TIME") {
      payload.date_preset = daysRange;
      const dr = resolveOneTimeDateRange();
      if (dr) {
        payload.since = dr.since;
        payload.until = dr.until;
      }
    }

    if (generationType === "PERIODICAL") {
      payload.frequency = frequency;
      
      // Add date ranges based on frequency for periodical reports
      let startDate, endDate;
      
      if (frequency === "WEEKLY") {
        // Last week (Monday to Sunday)
        startDate = dayjs().subtract(1, 'week').startOf('week').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'week').endOf('week').format("YYYY-MM-DD");
      } else if (frequency === "MONTHLY") {
        // Last month
        startDate = dayjs().subtract(1, 'month').startOf('month').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'month').endOf('month').format("YYYY-MM-DD");
      } else if (frequency === "QUARTERLY") {
        // Last quarter
        startDate = dayjs().subtract(1, 'quarter').startOf('quarter').format("YYYY-MM-DD");
        endDate = dayjs().subtract(1, 'quarter').endOf('quarter').format("YYYY-MM-DD");
      }
      
      if (startDate && endDate) {
        payload.since = startDate;
        payload.until = endDate;
      }
    }

    if (selectedPivotCol && selectedPivotValues.length > 0) {
      const validPivotValues = selectedPivotValues.filter(Boolean);

      // New structure: pivot_key and pivot_values
      payload.pivot_key = selectedPivotCol.id;
      payload.pivot_values = validPivotValues.map((v) => v.id);
      payload.generate_individual_reports = generateIndividualReports;
    }

    if (reportScope === "SELECTED_TOPICS" && selectedTopics?.length > 0) {
      payload.topic_ids = selectedTopics
        ?.filter(Boolean)
        .map((topic) => topic.id);
    }

    const promise = apiRequest("report/create", "POST", payload, true);
    const timeout = new Promise((resolve) => setTimeout(resolve, 6000));
    const result = await Promise.race([promise, timeout]);

    if (result?.validation_error || result?.error) {
      notification.error({
        message: result?.error ? result.error : "Unable to create report",
      });
      setCreateLoading(false);
    } else {
      notification.success({
        message: t("reports_downloads.25") || "Report created successfully",
      });
      resetForm();
      onClose();
      getReportsDownloads();
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setGenerationType(null);
    setReportName("");
    setReportKind(null);
    setSelectedGroup(null);
    setSelectedDashboards([]);
    setDaysRange("last7d");
    setReportDateRange([]);
    setSince("");
    setUntil("");
    setFrequency(null);
    setSelectedPivotCol(null);
    setPivotValues([]);
    setSelectedPivotValues([]);
    setGenerateIndividualReports(false);
    setReportScope(null);
    setSelectedTopics([]);
    setRecipientEmails([]);
    setEmailInput("");
    setCurrStep(0);
    setQuotaCheckResult(null);
    setQuotaCheckDeps(null);
    setDashboardSelectKey(0);
    setPivotValuesSelectKey(0);
  };

  useEffect(() => {
    if (open) {
      getGroups();
    }
  }, [open]);

  useEffect(() => {
    if (selectedGroup) {
      getDashboards();
      setSelectedDashboards([]);
      // Force dashboard dropdown to re-render
      setDashboardSelectKey(prev => prev + 1);
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedDashboards?.length > 0) {
      getPivots();
      // Reset pivot selection when dashboards change
      setSelectedPivotCol(null);
      setSelectedPivotValues([]);
      setPivotValues([]);
    }
  }, [selectedDashboards]);

  useEffect(() => {
    if (selectedPivotCol) {
      getPivotValues(selectedPivotCol.id);
      setSelectedPivotValues([]);
      // Force pivot values dropdown to re-render
      setPivotValuesSelectKey(prev => prev + 1);
    }
  }, [selectedPivotCol]);

  useEffect(() => {
    if (reportScope === "SELECTED_TOPICS" && selectedDashboards?.length > 0) {
      getTopics();
    }
  }, [reportScope, selectedDashboards]);

  // Track changes to quota-relevant parameters and reset quota check
  useEffect(() => {
    const currentDeps = JSON.stringify({
      dashboards: selectedDashboards?.map(d => d.id).sort(),
      dateRange: reportDateRange,
      frequency: frequency,
      generationType: generationType,
    });
    
    // Reset quota check result if dependencies changed
    if (quotaCheckDeps && quotaCheckDeps !== currentDeps) {
      setQuotaCheckResult(null);
    }
    
    setQuotaCheckDeps(currentDeps);
  }, [selectedDashboards, reportDateRange, frequency, generationType]);

  useEffect(() => {
    if (isQuotaCheckStep() && !quotaCheckResult && !quotaCheckLoading) {
      checkQuotaEstimate();
    }
  }, [currStep, reportKind, quotaCheckDeps]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && isValidEmail(trimmedEmail)) {
      if (!recipientEmails.includes(trimmedEmail)) {
        setRecipientEmails([...recipientEmails, trimmedEmail]);
        setEmailInput("");
      } else {
        notification.warning({
          message: "Email already added",
        });
      }
    } else {
      notification.error({
        message: "Please enter a valid email address",
      });
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setRecipientEmails(
      recipientEmails.filter((email) => email !== emailToRemove),
    );
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Handle preset selection
  const handlePresetSelection = (preset) => {
    setDaysRange(preset.id);
    const dateString = [
      dayjs(preset.value[0]).format("YYYY-MM-DD"),
      dayjs(preset.value[1]).format("YYYY-MM-DD"),
    ];
    setReportDateRange(dateString);
    setSince(dateString[0]);
    setUntil(dateString[1]);
  };

  // Handle custom selection
  const handleCustomSelection = () => {
    setDaysRange("custom");
    if (since && until) {
      setReportDateRange([since, until]);
    } else {
      // Set default custom range to today
      const today = dayjs().format("YYYY-MM-DD");
      setSince(today);
      setUntil(today);
      setReportDateRange([today, today]);
    }
  };

  // Helper function to get date range for periodical frequency
  const getFrequencyDateRange = (freq) => {
    let startDate, endDate;
    
    if (freq === "WEEKLY") {
      startDate = dayjs().subtract(1, 'week').startOf('week');
      endDate = dayjs().subtract(1, 'week').endOf('week');
    } else if (freq === "MONTHLY") {
      startDate = dayjs().subtract(1, 'month').startOf('month');
      endDate = dayjs().subtract(1, 'month').endOf('month');
    } else if (freq === "QUARTERLY") {
      startDate = dayjs().subtract(1, 'quarter').startOf('quarter');
      endDate = dayjs().subtract(1, 'quarter').endOf('quarter');
    }
    
    if (startDate && endDate) {
      return `(${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')})`;
    }
    return '';
  };

  const generationTypeOptions = [
    { id: "ONE_TIME", label: "One-time", value: "ONE_TIME" },
    { id: "PERIODICAL", label: "Periodical", value: "PERIODICAL" },
  ];

  const reportKindOptions = [
    { id: "STANDARD", label: "Standard report", value: "STANDARD" },
    { id: "AI_ADVANCED", label: "Advanced AI report", value: "AI_ADVANCED" },
  ];

  const frequencyOptions = [
    { 
      id: "WEEKLY", 
      label: `Weekly ${getFrequencyDateRange('WEEKLY')}`, 
      value: "WEEKLY" 
    },
    { 
      id: "MONTHLY", 
      label: `Monthly (end of month) ${getFrequencyDateRange('MONTHLY')}`, 
      value: "MONTHLY" 
    },
    { 
      id: "QUARTERLY", 
      label: `Quarterly ${getFrequencyDateRange('QUARTERLY')}`, 
      value: "QUARTERLY" 
    },
  ];

  const reportScopeOptions = [
    { id: "FULL", label: "Full-report", value: "FULL" },
    {
      id: "SELECTED_TOPICS",
      label: "Only for selected topics",
      value: "SELECTED_TOPICS",
    },
  ];

  const getSteps = () => {
    const steps = {};
    let stepIndex = 0;

    steps[stepIndex++] = [
      {
        title: "Generation Type",
        type: "radio-select",
        required: true,
        subtitle: "Choose report generation type:",
        options: generationTypeOptions,
        selectedValue: generationType,
        setSelectedValue: (val) => {
          setGenerationType(val.id);
          setFrequency(null);
          setDaysRange("last7d");
          if (val.id === "ONE_TIME") {
            const defaultPreset = rangePresets.find((p) => p.id === "last7d");
            if (defaultPreset) {
              const dateString = [
                dayjs(defaultPreset.value[0]).format("YYYY-MM-DD"),
                dayjs(defaultPreset.value[1]).format("YYYY-MM-DD"),
              ];
              setReportDateRange(dateString);
              setSince(dateString[0]);
              setUntil(dateString[1]);
            }
          } else {
            setReportDateRange([]);
          }
        },
        visible: true,
      },
    ];

    steps[stepIndex++] = [
      {
        title: "Report Name",
        type: "input",
        required: true,
        placeholder: "Enter report name*",
        subtitle: "Enter report name*",
        selectedValue: reportName,
        setSelectedValue: setReportName,
        visible: true,
      },
    ];

    steps[stepIndex++] = [
      {
        title: "Report Type",
        type: "radio-select",
        required: true,
        subtitle: "Choose report type:",
        options: reportKindOptions,
        selectedValue: reportKind,
        setSelectedValue: (val) => {
          setReportKind(val.id);
        },
        visible: true,
      },
    ];

    steps[stepIndex++] = [
      {
        title: "Group",
        type: "single-select",
        placeholder: "Choose group*",
        subtitle: "Choose group*",
        required: true,
        loading: groupsLoading,
        options: groups,
        selectedValue: selectedGroup,
        setSelectedValue: (val) => {
          setSelectedGroup(val);
          setSelectedDashboards([]);
        },
        visible: true,
      },
      {
        title: "Dashboards",
        type: "multi-select",
        placeholder: "Choose dashboards*",
        subtitle: "Choose dashboards*",
        required: true,
        loading: dashboardsLoading,
        options: dashboards,
        selectedValue: selectedDashboards,
        setSelectedValue: setSelectedDashboards,
        visible: !!selectedGroup,
        key: dashboardSelectKey, // Add key to force re-render
      },
    ];

    if (generationType === "ONE_TIME") {
      steps[stepIndex++] = [
        {
          title: "Date Period",
          type: "date-range",
          required: false, // Changed to false since Last 7 days is default
          subtitle: "Select date period:",
          visible: true,
        },
      ];
    } else if (generationType === "PERIODICAL") {
      steps[stepIndex++] = [
        {
          title: "Frequency",
          type: "radio-select",
          required: true,
          subtitle: "Choose frequency:",
          options: frequencyOptions,
          selectedValue: frequency,
          setSelectedValue: (val) => {
            setFrequency(val.id);
          },
          visible: true,
        },
      ];
    }

    if (pivotCols?.length > 0) {
      const pivotSteps = [
        {
          title: "Pivots",
          type: "single-select",
          subtitle: (
            <div className={styles.subtitleWithTooltip}>
              Choose Pivot Column
              <Tooltip
                placement="bottom"
                title="Select a column to filter or split reports by"
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
          ),
          placeholder: "Select Pivot Column",
          options: pivotCols,
          loading: pivotsLoading,
          selectedValue: selectedPivotCol,
          setSelectedValue: setSelectedPivotCol,
          visible: true,
          required: false,
        },
      ];

      if (selectedPivotCol) {
        pivotSteps.push({
          title: "Pivot Values",
          type: "multi-select",
          placeholder: "Select Pivot Values*",
          subtitle: "Select one or all pivot values*",
          required: true,
          loading: pivotValuesLoading,
          options: pivotValues,
          selectedValue: selectedPivotValues,
          setSelectedValue: setSelectedPivotValues,
          visible: true,
          key: pivotValuesSelectKey, // Add key to force re-render
        });

        pivotSteps.push({
          title: "",
          type: "checkbox",
          label: "Generate an individual report for each selection",
          selectedValue: generateIndividualReports,
          setSelectedValue: setGenerateIndividualReports,
          visible: true,
          required: false,
        });
      }

      steps[stepIndex++] = pivotSteps;
    }

    const scopeSteps = [
      {
        title: "Report Scope",
        type: "radio-select",
        required: true,
        subtitle: "Choose report scope:",
        options: reportScopeOptions,
        selectedValue: reportScope,
        setSelectedValue: (val) => {
          setReportScope(val.id);
          if (val.id !== "SELECTED_TOPICS") {
            setSelectedTopics([]);
          }
        },
        visible: true,
      },
    ];

    if (reportScope === "SELECTED_TOPICS") {
      scopeSteps.push({
        title: "Topics",
        type: "topic-checklist",
        subtitle: "Choose topics*",
        required: true,
        loading: topicsLoading,
        options: topics,
        selectedValue: selectedTopics,
        setSelectedValue: setSelectedTopics,
        visible: true,
      });
    }

    steps[stepIndex++] = scopeSteps;

    steps[stepIndex++] = [
      {
        title: "Recipient Emails",
        type: "email-input",
        required: false,
        placeholder: "Enter recipient email address (optional)",
        subtitle: "Enter recipient email address (optional)",
        selectedValue: emailInput,
        setSelectedValue: setEmailInput,
        visible: true,
      },
    ];

    if (reportKind === "AI_ADVANCED") {
      steps[stepIndex++] = [
        {
          title: "AI Quota Check",
          type: "quota-check",
          required: false,
          subtitle: "Checking your AI quota availability...",
          visible: true,
        },
      ];
    }

    return steps;
  };

  const steps = getSteps();
  const stepKeys = Object.keys(steps)
    .map(Number)
    .sort((a, b) => a - b);

  useEffect(() => {
    if (!stepKeys.includes(currStep)) {
      setCurrStep(stepKeys[0] || 0);
    }
  }, [stepKeys.join(",")]);

  const isStepValid = () => {
    const currentSteps = steps[currStep] || [];

    for (const stepObj of currentSteps) {
      if (!stepObj.required || !stepObj.visible) continue;

      if (stepObj.type === "quota-check") {
        continue;
      }

      if (stepObj.type === "email-input") {
        continue;
      }

      if (stepObj.type === "date-range") {
        // Date range is always valid since Last 7 days is default
        continue;
      } else if (stepObj.type === "checkbox" && generateIndividualReports) {
        if (!selectedPivotCol) return false;
      } else if (
        !stepObj.selectedValue ||
        (typeof stepObj.selectedValue === "string" &&
          stepObj.selectedValue.trim() === "") ||
        (Array.isArray(stepObj.selectedValue) &&
          stepObj.selectedValue.length === 0)
      ) {
        return false;
      }
    }

    return true;
  };

  const isQuotaCheckStep = () => {
    const lastStep = stepKeys[stepKeys.length - 1];
    const currentStepConfig = steps[currStep];
    return (
      reportKind === "AI_ADVANCED" &&
      currStep === lastStep &&
      currentStepConfig?.[0]?.type === "quota-check"
    );
  };

  const isQuotaSufficient = () => {
    if (!isQuotaCheckStep()) return true;
    if (!quotaCheckResult) return false;

    return (
      quotaCheckResult?.sufficient === true ||
      quotaCheckResult?.unlimited === true
    );
  };

  const handleNextClick = () => {
    const lastStep = stepKeys[stepKeys.length - 1];

    if (currStep === lastStep) {
      handleCreateReport();
    } else {
      const nextStepIdx = stepKeys.findIndex((k) => k === currStep) + 1;
      setCurrStep(stepKeys[nextStepIdx]);
    }
  };

  const getModalWidth = () => {
    if (isMobileView) {
      return "95%";
    }
    return "650px";
  };

  return (
    <>
      <Modal
        showModal={open}
        showSaveButton={false}
        showCancelButton={false}
        onClose={() => {
          resetForm();
          onClose();
        }}
        titleAsset={""}
        widthofmodal={getModalWidth()}
        className={styles.PopupSequence}
        title={
          <div className={styles.popupHeading}>
            {"Create New Report"}
            {steps[currStep]?.[0]?.title
              ? ` > ${steps[currStep][0].title}`
              : ""}
          </div>
        }
        description={steps[currStep]?.[0]?.description}
      >
        <div className={styles.Popup}>
          {steps[currStep]?.map((step, index) => (
            <div key={step.key || index} className={styles.stepsWrapper}>
              {step?.visible && step?.subtitle && (
                <div className={styles.popupSubTitle}>
                  {step?.subtitle}
                  <span
                    className={
                      step?.required ? styles.required : styles.optional
                    }
                  >
                    {step?.required ? "" : ""}
                  </span>
                </div>
              )}
              {step?.type === "input" && step?.visible && (
                <input
                  placeholder={step?.placeholder}
                  value={step?.selectedValue}
                  onChange={(e) => step?.setSelectedValue(e.target.value)}
                  className={styles.reportNameInput}
                />
              )}
              {step?.type === "number-input" && step?.visible && (
                <InputNumber
                  placeholder={step?.placeholder}
                  value={step?.selectedValue}
                  onChange={(val) => step?.setSelectedValue(val)}
                  min={step?.min || 0}
                  max={step?.max}
                  className={styles.reportNameInput}
                  style={{ width: "100%" }}
                />
              )}
              {step?.type === "single-select" && step?.visible && (
                <div className={styles.selectWrapper}>
                  <SingleSelect
                    key={step.key}
                    options={step?.options}
                    selectedOptions={step?.selectedValue}
                    setSelectedOptions={step?.setSelectedValue}
                    placeholder={
                      <span className={styles.actionsPlaceholder}>
                        {step?.placeholder}
                      </span>
                    }
                    width={550}
                    height={48}
                    optionsLoading={step?.loading}
                    allowClearSelection
                    popupStyling
                  />
                </div>
              )}
              {step?.type === "topic-checklist" && step?.visible && (
                <div className={styles.topicChecklistWrap}>
                  <SearchableTopicChecklist
                    options={(step.options || []).map((o) => ({
                      id: o.id,
                      label: o.label,
                    }))}
                    value={(step.selectedValue || []).map((x) => x?.id).filter(Boolean)}
                    onChange={(ids) => {
                      const objs = ids
                        .map((id) => step.options?.find((opt) => opt.id === id))
                        .filter(Boolean)
                        .map((o) => ({
                          id: o.id,
                          name: o.label,
                          value: o.value,
                        }));
                      step.setSelectedValue(objs);
                    }}
                    loading={step.loading}
                    placeholder={t("welcome.searchAvailableTopics")}
                  />
                </div>
              )}
              {step?.type === "multi-select" && step?.visible && (
                <div className={styles.selectWrapper}>
                  <MultipleSelect
                    key={step.key}
                    showSearch={true}
                    placeholder={step?.placeholder}
                    tagCount={"responsive"}
                    options={step?.options}
                    selectedOptions={step?.selectedValue
                      ?.map((item) => item?.id)
                      .filter(Boolean)}
                    enableSelectAll
                    width={550}
                    height={48}
                    optionsLoading={step?.loading}
                    setSelectedOptions={(values) => {
                      const selectedValues = values
                        .map((item) => {
                          const option = step?.options?.find(
                            (opt) => opt.id === item,
                          );
                          if (option) {
                            return {
                              id: option.id,
                              name: option.label,
                              value: option.value,
                            };
                          }
                          return null;
                        })
                        .filter(Boolean);
                      step.setSelectedValue(selectedValues);
                    }}
                    popupStyling
                  />
                </div>
              )}
              {step?.type === "date-range" && step?.visible && (
                <div className={styles.dateFilterEmbedded}>
                  <div className={styles.quickDates}>
                    <Radio.Group
                      className={styles.radioWrapper}
                      value={daysRange}
                    >
                      <Space direction="vertical">
                        {rangePresets.map((val) => (
                          <Radio
                            value={val.id}
                            key={val.id}
                            checked={daysRange === val.id}
                            onClick={() => handlePresetSelection(val)}
                          >
                            {val.label}
                          </Radio>
                        ))}
                        <Radio
                          value="custom"
                          checked={daysRange === "custom"}
                          onClick={handleCustomSelection}
                        >
                          {t("filter.20") || "Custom"}
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </div>
                  {daysRange === "custom" && (
                    <div className={styles.datePickerEmbedded}>
                      <DatePicker
                        value={since ? dayjs(since, "YYYY-MM-DD") : null}
                        onChange={(date, dateString) => {
                          setSince(dateString);
                        }}
                        allowClear={false}
                        placeholder="Start Date"
                        style={{ width: "100%" }}
                      />
                      <DatePicker
                        value={until ? dayjs(until, "YYYY-MM-DD") : null}
                        onChange={(date, dateString) => {
                          setUntil(dateString);
                        }}
                        allowClear={false}
                        placeholder="End Date"
                        style={{ width: "100%", marginTop: "12px" }}
                      />
                    </div>
                  )}
                </div>
              )}
              {step?.type === "radio-select" && step?.visible && (
                <Space
                  direction="vertical"
                  size="middle"
                  className={styles.radioSelectSpace}
                >
                  {step?.options.map((option) => (
                    <Radio
                      key={option.id}
                      value={option}
                      checked={step?.selectedValue === option.id}
                      onChange={(e) => {
                        step.setSelectedValue(e.target.value);
                      }}
                    >
                      {option.label}
                    </Radio>
                  ))}
                </Space>
              )}
              {step?.type === "checkbox" && step?.visible && (
                <Checkbox
                  checked={step?.selectedValue}
                  onChange={(e) => step?.setSelectedValue(e.target.checked)}
                >
                  {step?.label}
                </Checkbox>
              )}
              {step?.type === "email-input" && step?.visible && (
                <div className={styles.emailSection}>
                  <div className={styles.emailInputWrapper}>
                    <input
                      placeholder={step?.placeholder}
                      value={step?.selectedValue}
                      onChange={(e) => step?.setSelectedValue(e.target.value)}
                      onKeyPress={handleEmailKeyPress}
                      onBlur={handleAddEmail}
                      className={styles.reportNameInput}
                    />
                  </div>
                  {recipientEmails.length > 0 && (
                    <div className={styles.emailTags}>
                      {recipientEmails.map((email) => (
                        <Tag
                          key={email}
                          closable
                          onClose={() => handleRemoveEmail(email)}
                          closeIcon={<CloseOutlined />}
                          className={styles.emailTag}
                        >
                          {email}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {step?.type === "quota-check" && step?.visible && (
                <div className={styles.quotaCheckContainer}>
                  {quotaCheckLoading ? (
                    <div className={styles.quotaLoading}>
                      <span>Checking your AI quota...</span>
                    </div>
                  ) : !quotaCheckResult ? (
                    <div className={styles.quotaLoading}>
                      <span>Preparing to check quota...</span>
                    </div>
                  ) : quotaCheckResult?.sufficient === true ? (
                    <div className={styles.quotaSuccess}>
                      <div className={styles.quotaMainMessage}>
                        {quotaCheckResult?.unlimited ? (
                          <div className={styles.quotaUnlimited}>
                            <span className={styles.quotaIcon}>✓</span>
                            <span className={styles.quotaText}>
                              You have unlimited {quotaCheckResult?.quota_name || "AI credits"}
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className={styles.quotaStatusRow}>
                              <span className={styles.quotaIcon}>✓</span>
                              <span className={styles.quotaStatusText}>Quota Check Passed</span>
                            </div>
                            <div className={styles.quotaStats}>
                              <div className={styles.quotaStat}>
                                <span className={styles.quotaStatLabel}>Current Quota</span>
                                <span className={styles.quotaStatValue}>{quotaCheckResult?.current_quota || 0}</span>
                              </div>
                              <div className={styles.quotaStatDivider}>→</div>
                              <div className={styles.quotaStat}>
                                <span className={styles.quotaStatLabel}>Will Use</span>
                                <span className={styles.quotaStatValue}>{quotaCheckResult?.required_amount || 0}</span>
                              </div>
                              <div className={styles.quotaStatDivider}>→</div>
                              <div className={styles.quotaStat}>
                                <span className={styles.quotaStatLabel}>Remaining</span>
                                <span className={styles.quotaStatValue}>{quotaCheckResult?.remaining_quota || 0}</span>
                              </div>
                            </div>
                            {quotaCheckResult?.total_review_count > 0 && (
                              <div className={styles.quotaReviewInfo}>
                                Processing {quotaCheckResult.total_review_count} review{quotaCheckResult.total_review_count !== 1 ? 's' : ''} across {quotaCheckResult?.accessible_dashboards_count || 1} dashboard{quotaCheckResult?.accessible_dashboards_count !== 1 ? 's' : ''}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : quotaCheckResult?.sufficient === false ? (
                    <div className={styles.quotaError}>
                      <div className={styles.quotaMainMessage}>
                        <div className={styles.quotaStatusRow}>
                          <span className={styles.quotaErrorIcon}>✕</span>
                          <span className={styles.quotaErrorText}>Insufficient Quota</span>
                        </div>
                        <div className={styles.quotaErrorStats}>
                          <div className={styles.quotaErrorStat}>
                            <span className={styles.quotaErrorStatLabel}>Available</span>
                            <span className={styles.quotaErrorStatValue}>{quotaCheckResult?.current_quota || 0}</span>
                          </div>
                          <div className={styles.quotaErrorStatDivider}>/</div>
                          <div className={styles.quotaErrorStat}>
                            <span className={styles.quotaErrorStatLabel}>Required</span>
                            <span className={styles.quotaErrorStatValue}>{quotaCheckResult?.required_amount || 0}</span>
                          </div>
                        </div>
                        {quotaCheckResult?.total_review_count > 0 && (
                          <div className={styles.quotaReviewInfo}>
                            Attempting to process {quotaCheckResult.total_review_count} review{quotaCheckResult.total_review_count !== 1 ? 's' : ''} across {quotaCheckResult?.accessible_dashboards_count || 1} dashboard{quotaCheckResult?.accessible_dashboards_count !== 1 ? 's' : ''}
                          </div>
                        )}
                        <div className={styles.quotaErrorAction}>
                          Please contact your administrator to increase your quota.
                        </div>
                      </div>
                    </div>
                  ) : quotaCheckResult?.applicable === false ? (
                    <div className={styles.quotaWarning}>
                      <div className={styles.quotaMessage}>
                        {resolveQuotaNotApplicableMessage(
                          quotaCheckResult,
                          t,
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.footerBtn}
            type="primary"
            onClick={() => {
              if (currStep === stepKeys[0]) {
                resetForm();
                onClose();
              } else {
                const prevStepIdx =
                  stepKeys.findIndex((k) => k === currStep) - 1;
                setCurrStep(stepKeys[prevStepIdx]);
              }
            }}
          >
            {cancelButtonText ? cancelButtonText : t("button.3")}
          </Button>
          <Button
            className={styles.footerBtn}
            type="primary"
            onClick={handleNextClick}
            loading={createLoading || quotaCheckLoading}
            disabled={
              !isStepValid() || (isQuotaCheckStep() && !isQuotaSufficient())
            }
          >
            {(() => {
              const lastStep = stepKeys[stepKeys.length - 1];
              return currStep === lastStep
                ? "Create Report"
                : saveButtonText
                ? saveButtonText
                : t("button.30");
            })()}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default CreateReport;